//WA pro #27385
//@ts-ignore
//import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import React, { ComponentType } from "react";
import { render, unmountComponentAtNode } from "react-dom";

import { FAKE_COMPONENT_NOEDIT_VALUE_LOC_KEY, FakeContainerProps } from "@multiweb/reactComponents";
import { loadLocalizationIfNeeded } from "@multiweb/reactCore/i18n";
import { createRootComponent, RootComponentClass, StoreManager } from "@multiweb/reactSetup";
import { doNothing } from "@multiweb/reactUtils";
import { AnyConstructor } from "Global/Base/types";
import { AppContext } from "../../../App";
import { IControl, IGenericControl, ILocalizable, IMultiEdit } from "../../UI/UI/Controls/ControlInterfaces";

/**
 * Přidá podporu pro hromadnou editaci k zadané komponentě. Podporuje původní komponenty i konektory pro react komponenty.
 * Komponenta nebude reagovat na zadnou interakci a zustane porad v needitovatelnem rezimu.
 *
 * Poznámka:
 * React komponenty by ideálně měly mít podporu pro hromadnou editaci vestavěnou, takže po přepsání všech komponent již nebude
 * tento mixin potřebný. V react komponentách lze využít pomocnou komponentu {@link MultiEditWrapper}.
 *
 * @param Base Základní komponenta, která se rozšíří o podporu hromadné editace.
 * @param FakeComponent Fake komponenta zobrazovaná po aktivaci hromadné editace.
 */
export const NoEditMultiEditMixin = <T extends AnyConstructor<
        IControl & IGenericControl & ILocalizable
    >>(Base: T, FakeComponent: ComponentType<FakeContainerProps>) =>
        class NoEditMultiEditMixin extends Base implements IMultiEdit {
            /**
             * Css třída pro skrytí elementu reálné komponenty při aktivní hromadné editaci.
             * Pro skrytí nestačí nastavení hidden atributu, protože ten může být potlačen
             * nastavením display pravidla ve stylech komponenty.
             */
            public static readonly HIDDEN_CLASS = "multi-edit-mixin-hidden";

            /**
             * Příznak, zda je aktivní hromadná editace.
             */
            public _multiEditActive = false;

            /**
             * Element, do kterého se vykresluje fake komponenta.
             */
            public _fakeRootElement: HTMLElement;

            public get touched(): boolean {
                return false;
            }

            /** Kořenová komponenta, do které se vkládá předaná react komponenta (parametr FakeComponent). Poskytuje pro své potomky přístup k redux stavu a zachytává chyby z komponent, reducerů a ság. */
            public readonly _rootComponent: RootComponentClass;

            constructor(...args: any[]) {
                super(...args);
                const isDev = !!AppContext.instance.configuration.developmentMode;
                StoreManager.initializeStore(isDev);
                this._rootComponent = createRootComponent(isDev);
            }

            public get multiEditActive(): boolean {
                return this._multiEditActive;
            }

            public set multiEditActive(value) {
                this._multiEditActive = value;
                if (this._multiEditActive) {
                    loadLocalizationIfNeeded(FAKE_COMPONENT_NOEDIT_VALUE_LOC_KEY);
                    this._replaceWithFake();
                }
                if (!this._multiEditActive) { //reset příznaku při vypnutí hromadné editace
                    this._returnToOriginal();
                }
            }

            public override set locKey(value: string) {
                super.locKey = value;
                loadLocalizationIfNeeded(value);
                this._renderFakeComponentIfPresent();
            }

            public override get locKey(): string {
                return super.locKey;
            }

            public override set isVisuallyEncrypted(value: boolean) {
                super.isVisuallyEncrypted = value;
                this._renderFakeComponentIfPresent();
            }

            public override get isVisuallyEncrypted(): boolean {
                return super.isVisuallyEncrypted;
            }

            /**
             * Nahradí originální komponentu za fake komponentu, pokud ještě nahrazená není.
             */
            public _replaceWithFake(): void {
                if (this._fakeRootElement) {
                    return; // fake komponenta je již zapojená
                }

                this._fakeRootElement = document.createElement("div");
                this._fakeRootElement.classList.add("fake-root-element");
                // Některé layouty, např. v tabulce, nastavují pevnou šířku na element. Fake komponenta tuto šířku musí převzít.
                this._fakeRootElement.style.width = this.element.style.width;
                this.element.insertAdjacentElement("afterend", this._fakeRootElement);
                this.element.hidden = true;
                this.element.classList.add(NoEditMultiEditMixin.HIDDEN_CLASS);
                this._renderFakeComponentIfPresent();
            }

            /**
             * Vypočítá hodnotu disabled prop na základě jiných příznaků.
             */
            public _computeDisabled(): boolean {
                return this.isVisuallyEncrypted;
            }

            /**
             * Provede vykreslení fake komponenty, pokud bylo zobrazování přepnuto na fake komponentu.
             * V opačném případě nic nedělá.
             */
            public _renderFakeComponentIfPresent(): void {
                if (!this._fakeRootElement) {
                    return;
                }

                render(
                    <this._rootComponent>
                        <FakeComponent
                            onClick={doNothing}
                            onKeyDown={doNothing}
                            locKey={this.locKey}
                            disabled={this._computeDisabled()}
                            isEditing={false}
                        />
                    </this._rootComponent>, this._fakeRootElement);
            }

            /**
             * Odstraní fake komponentu a vrátí originál, pokud je fake komponenta zobrazená.
             */
            public _returnToOriginal(): void {
                if (!this._fakeRootElement) {
                    return;
                }
                unmountComponentAtNode(this._fakeRootElement);
                this._fakeRootElement.remove();
                this._fakeRootElement = undefined;
                this.element.hidden = false;
                this.element.classList.remove(NoEditMultiEditMixin.HIDDEN_CLASS);
            }
        };
